import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  Input,
  Label,
  FormGroup,
  Form,
} from "reactstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { addSource, getSourceDataById, updateSource } from "model/SourceModel";

/* -------------------- Yup Schema -------------------- */
const sourceSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .required("Name is required"),
  status: Yup.boolean(),
});

const SourceAddEditModal = ({
  setSourceAddEditModalOpen,
  sourceAddEditModalOpen,
  getSourceList,
  itemId,
  setFormData,
  formData,
  totalData
}) => {
  const [formKey, setFormKey] = useState(0);
  const [errors, setErrors] = useState({});

  const closeModal = () => {
    setSourceAddEditModalOpen(false);
    setErrors({});
  };

  /* -------------------- Change Handler -------------------- */
  const handleChange = async (e) => {
    const { name, type, checked, value } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: fieldValue,
    }));

    if (!sourceSchema.fields[name]) return;

    try {
      await Yup.reach(sourceSchema, name).validate(fieldValue);
      setErrors((prev) => ({ ...prev, [name]: "" }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, [name]: err.message }));
    }
  };

  /* -------------------- Submit -------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await sourceSchema.validate(formData, { abortEarly: false });
      setErrors({});
    } catch (err) {
      const newErrors = {};
      err.inner.forEach((error) => {
        newErrors[error.path] = error.message;
      });
      setErrors(newErrors);
      return;
    }

    const params = {
      // Keep compatibility with existing backend field names.
      name: formData?.name,
      status: formData?.status ? "active" : "deactive",
    };

    let response;
    if (itemId) {
      response = await updateSource(itemId, params);
    } else {
      response = await addSource(params);
    }

    if (response?.success) {
      toast.success(response?.message);
      closeModal();
      getSourceList();
    } else {
      toast.error(response?.message);
    }
  };

  /* -------------------- Edit Fetch -------------------- */
  const getFetchSourceById = async (id) => {
    const response = await getSourceDataById({ Id: id });
    if (response?.data) {
      setFormData({
        name: response?.data?.name || response?.data?.jobCategoryName || "",
        status:
          response?.data?.status === "active" ||
          response?.data?.jobCategoryStatus === "active",
      });
      setFormKey((k) => k + 1);
    }
  };

  useEffect(() => {
    if (itemId) {
      getFetchSourceById(itemId);
    }
  }, [itemId]);

  return (
    <Modal
      isOpen={sourceAddEditModalOpen}
      toggle={closeModal}
      backdrop="static"
      className="modal-full-height"
      centered
    >
      <ModalBody
        key={formKey}
        className="p-4"
        style={{ maxHeight: "95vh", overflowY: "auto" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold mb-0">{itemId? 'Edit' : 'Create'} Source </h5>
          <Button close onClick={closeModal} />
        </div>
        <hr />

        <Form onSubmit={handleSubmit}>
          {/* Name */}
          <FormGroup>
            <Label>Name <span className="text-danger">*</span></Label>
            <Input
              type="text"
              name="name"
              defaultValue={formData?.name}
              onChange={handleChange}
            />
            {errors.name && (
              <small className="text-danger">{errors.name}</small>
            )}
          </FormGroup>

          {/* Status */}
          <FormGroup>
            <Label>Status <span className="text-danger">*</span></Label>
            <div className="input-box d-flex align-items-center justify-content-between">
              <span className="text-muted">Type</span>
              <div className="d-flex align-items-center">
                <strong className="me-2">
                  {formData?.status ? "active" : "deactive"}
                </strong>
                <div className="form-switch">
                  <Input
                    type="switch"
                    name="status"
                    defaultChecked={formData?.status}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </FormGroup>

          <div className="text-end mt-3">
            <Button color="primary" type="submit">
            {itemId? 'Update' : 'Save'}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default SourceAddEditModal;
